import { useLayoutEffect, useRef } from "react";
import type { Swiper as SwiperClass } from "swiper/types";

/**
 * `swiper/react`'s own instance teardown runs in a passive `useEffect`, which
 * fires *after* React has already removed the old DOM subtree on unmount. In
 * `loop: true` mode Swiper continuously clones/reparents slide nodes into the
 * `.swiper-wrapper` outside of React's bookkeeping (worse with continuous
 * `autoplay`, since that churn never stops while the carousel is mounted), so
 * whichever moment a route change unmounts the carousel is effectively
 * random relative to that churn — and if a clone is mid-flight, React's
 * recorded parent for a slide no longer matches its live parent, throwing
 * "Failed to execute 'removeChild' on 'Node': The node to be removed is not
 * a child of this node."
 *
 * `useLayoutEffect`'s cleanup runs synchronously as part of the same commit
 * that deletes the subtree, before that removal — so destroying the Swiper
 * instance here (independent of, and ahead of, the library's own passive
 * cleanup) unwinds the loop clones before React ever touches the DOM.
 *
 * Usage: `const { onSwiper } = useSwiperEarlyDestroy(); <Swiper onSwiper={onSwiper} ...>`
 */
export function useSwiperEarlyDestroy() {
  const swiperRef = useRef<SwiperClass | null>(null);

  useLayoutEffect(() => {
    return () => {
      // deleteInstance, cleanStyles — undo everything Swiper did to the DOM.
      swiperRef.current?.destroy(true, true);
      swiperRef.current = null;
    };
  }, []);

  return {
    onSwiper: (instance: SwiperClass) => {
      swiperRef.current = instance;
    },
  };
}
